import { Box } from '@mui/material';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

import DisplayNumber from '@commons/DisplayNumber';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import { calculateChange, calculateColor, METRIC_TYPE } from '../utils';

const ChartTooltip = ({ payload, label, selectedMetric, options, payloadLabel, displayTotal }) => {
  if (!payload || !payload.length) {
    return null;
  }

  const [change, setChange] = useState(null);
  const [color, setColor] = useState(ENUM_COLORS.BLACK);
  const [isPercentage, setIsPercentage] = useState(false);
  const [hasDecimals, setHasDecimals] = useState(false);
  const [isCurrency, setIsCurrency] = useState(false);
  const [total, setTotal] = useState(null);

  useEffect(() => {
    const selectedOption = options.find((option) => option.id === selectedMetric?.id);

    const isMetricCurrency =
      selectedOption?.type === METRIC_TYPE.CURRENCY ||
      selectedOption?.type === METRIC_TYPE.CURRENCY_WITH_DECIMALS;
    const isMetricPercentage = selectedOption?.type === METRIC_TYPE.PERCENTAGE;
    const isCurrencyWithDecimals = selectedOption?.type === METRIC_TYPE.CURRENCY_WITH_DECIMALS;
    const isMetricIncreasePositive = !selectedOption?.isNegative;

    setIsPercentage(isMetricPercentage);
    setIsCurrency(isMetricCurrency);
    setHasDecimals(isCurrencyWithDecimals || isMetricPercentage);

    const currentEntry = payload.find((entry) => entry.dataKey.startsWith('current'));
    const previousEntry = payload.find((entry) => entry.dataKey.startsWith('previous'));

    const calculatedChange =
      currentEntry && previousEntry
        ? calculateChange(currentEntry.value, previousEntry.value, isMetricPercentage)
        : null;
    const calculatedColor = calculateColor(calculatedChange, isMetricIncreasePositive);

    setChange(calculatedChange);
    setColor(calculatedColor);
    setTotal(payload.reduce((sum, entry) => sum + entry.value, 0));
  }, [payload, selectedMetric]);

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
    >
      {!payloadLabel && (
        <Text
          color={ENUM_COLORS.BLACK}
          font={ENUM_FONTS.TEXT_BIG_BOLD_HEIGHT_14}
          style={{ marginBottom: '8px' }}
        >
          {label}
        </Text>
      )}
      {payload.map((entry, index) => (
        <Text
          font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}
          key={index}
          style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}
        >
          <Box
            sx={{
              backgroundColor: entry.color,
              borderRadius: '2px',
              height: 12,
              mr: 1,
              width: 12,
            }}
          />
          {payloadLabel && (
            <Text
              font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}
              key={index}
              style={{ color: entry.color, marginRight: '4px' }}
            >
              {entry?.payload?.[payloadLabel]?.[
                entry.dataKey.startsWith('current') ? 'current' : 'previous'
              ] + ':'}
            </Text>
          )}
          <DisplayNumber
            displayCurrencyCode={isCurrency}
            number={isPercentage ? Math.round(entry.value * 100) / 100 : entry.value}
            style={{ marginLeft: '4px' }}
            withoutDecimals={!hasDecimals}
          />
          {isPercentage && '%'}
        </Text>
      ))}

      {/* Display change if calculated */}
      {!isNaN(change) && change !== null && (
        <Text
          color={color}
          font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: '4px',
          }}
        >
          {change >= 0 && '+'}
          <DisplayNumber color={color} number={change} withoutDecimals={false} />
          {isPercentage ? i18next.t('ANALYSIS.PERFORMANCE.PTS') : '%'}
        </Text>
      )}

      {/* Display total if requested */}
      {displayTotal && (
        <Box
          sx={{
            borderTop: '1px solid #e0e0e0',
            marginTop: '8px',
            paddingTop: '8px',
          }}
        >
          <Text
            color={ENUM_COLORS.DARK}
            font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}
            style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}
          >
            {i18next.t('ANALYSIS.PERFORMANCE.TOTAL')}:{' '}
            <DisplayNumber
              displayCurrencyCode={isCurrency}
              number={isPercentage ? Math.round(total * 100) / 100 : total}
              style={{ marginLeft: '4px' }}
              withoutDecimals={!hasDecimals}
            />
            {isPercentage && '%'}
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default ChartTooltip;
